/**
 * API Load Tracker Utility
 * Tracks partial load failures and displays consolidated warnings to users
 */

import { showToast } from '../components/Toast'

/**
 * Process Promise.allSettled results and track failures
 * Returns the data array with empty arrays for failed loads, plus a list of what failed
 */
export function trackLoads(
  results: PromiseSettledResult<unknown>[],
  names: string[]
): { data: unknown[]; failedLoads: string[] } {
  const data: unknown[] = []
  const failedLoads: string[] = []

  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      data.push(result.value)
    } else {
      // Return empty array as fallback for failed loads
      data.push([])
      failedLoads.push(names[i])
    }
  })

  return { data, failedLoads }
}

/**
 * Show a user-friendly warning toast when some data fails to load
 */
export function showPartialLoadWarning(failedLoads: string[]): void {
  if (failedLoads.length === 0) return

  if (failedLoads.length === 1) {
    showToast(`Failed to load ${failedLoads[0]}. Try refreshing.`, 'warning')
  } else if (failedLoads.length <= 3) {
    showToast(`Some data couldn't be loaded: ${failedLoads.join(', ')}. Try refreshing.`, 'warning')
  } else {
    showToast(`Multiple data sources failed to load. Try refreshing the page.`, 'warning')
  }
}

/**
 * Combined helper: process results and show warning if needed
 * Returns just the data array for easy destructuring
 * Note: Returns unknown[] - caller should cast to appropriate tuple type
 */
export function processLoadResults(
  results: PromiseSettledResult<unknown>[],
  names: string[],
  showWarning: boolean = true
): unknown[] {
  const { data, failedLoads } = trackLoads(results, names)

  if (showWarning && failedLoads.length > 0) {
    showPartialLoadWarning(failedLoads)
  }

  return data
}

/**
 * Simpler helper for cases where you just want to log failures without user toast
 * Useful for background operations or secondary data that isn't critical
 * Note: Returns unknown[] - caller should cast to appropriate tuple type
 */
export function processLoadResultsSilent(
  results: PromiseSettledResult<unknown>[],
  names: string[]
): unknown[] {
  return results.map((result, i) => {
    if (result.status === 'fulfilled') {
      return result.value
    } else {
      console.warn(`[${names[i]}] Background load failed:`, result.reason)
      return []
    }
  })
}

/**
 * Collect warnings from Promise.allSettled results.
 * Returns null (not []) for failed supplemental sources — no data hiding.
 * Logs real errors to console and collects human-readable warnings.
 */
export function collectWarnings<T extends readonly unknown[]>(
  results: { [K in keyof T]: PromiseSettledResult<T[K]> },
  labels: string[]
): { values: { [K in keyof T]: T[K] | null }; warnings: string[] } {
  const values: unknown[] = []
  const warnings: string[] = []
  ;(results as PromiseSettledResult<unknown>[]).forEach((r, i) => {
    if (r.status === 'fulfilled') {
      values.push(r.value)
    } else {
      values.push(null)
      warnings.push(`${labels[i]} unavailable`)
    }
  })
  return { values, warnings } as any
}
